class CartAddonToggle {
  constructor() {
    this.activeRequests = new Map();
    this.cartUpdateUnsubscriber = null;
    this.init();
  }

  init() {
    document.addEventListener(
      'change',
      this.handleChange.bind(this)
    );

    document.addEventListener(
      'click',
      this.handleClick.bind(this)
    );

    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source !== 'cart-addon-toggle') {
          this.handleCartUpdated();
        }
      }
    );

    document.addEventListener(
      'cartUpdated',
      this.handleCartUpdated.bind(this)
    );
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleChange(event) {
    const checkbox = event.target;

    if (
      !checkbox.classList.contains(
        'cart-addon-toggle__checkbox'
      )
    ) {
      return;
    }

    const key =
      `${checkbox.dataset.variantId}-` +
      `${checkbox.dataset.mainVariantId}`;

    if (this.activeRequests.has(key)) {
      event.preventDefault();
      checkbox.checked = !checkbox.checked;
      return;
    }

    this.toggleAddon(checkbox);
  }

  handleClick(event) {
    const header = event.target.closest(
      '[data-toggle-header]'
    );

    if (
      !header ||
      event.target.closest(
        '.cart-addon-toggle__checkbox-wrapper'
      )
    ) {
      return;
    }

    this.toggleDetails(header);
  }

  async toggleAddon(checkbox) {
    const toggle = checkbox.closest(
      '.cart-addon-toggle'
    );

    const variantId = checkbox.dataset.variantId;
    const mainVariantId =
      checkbox.dataset.mainVariantId;
    const mainProductId =
      checkbox.dataset.mainProductId;

    const key = `${variantId}-${mainVariantId}`;

    toggle?.classList.add('is-loading');
    this.activeRequests.set(key, true);

    try {
      const isChecked = checkbox.checked;

      const quantity =
        parseInt(
          checkbox.dataset.mainQuantity,
          10
        ) || 1;

      let result;

      if (isChecked) {
        result = await this.addAddon(
          variantId,
          mainVariantId,
          mainProductId,
          quantity
        );
      } else {
        result = await this.removeAddon(
          variantId,
          mainVariantId,
          mainProductId
        );
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-addon-toggle',
        cartData: result,
        action: isChecked ? 'add' : 'remove',
        variantId
      });

      await window.helmUpdateCartCount?.();
    } catch (error) {
      console.error(
        'Failed to toggle addon:',
        error
      );

      checkbox.checked = !checkbox.checked;

      toggle?.classList.remove('is-loading');

      this.showError(
        toggle,
        error.message || 'Failed to update cart'
      );
    } finally {
      this.activeRequests.delete(key);
    }
  }

  async addAddon(
    variantId,
    mainVariantId,
    mainProductId,
    quantity
  ) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity,
            properties: {
              _upsell_of: mainVariantId,
              _bound_to_product: mainProductId,
              _is_hidden_upsell: 'true'
            }
          }
        ]
      })
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        errorData.description ||
        `Failed to add addon: ${response.status}`
      );
    }

    return response.json();
  }

  async removeAddon(
    variantId,
    mainVariantId,
    mainProductId
  ) {
    const cart = await this.getCart();

    const addon = cart.items.find((item) => {
      return (
        String(item.variant_id) ===
          String(variantId) &&
        String(item.properties?._upsell_of) ===
          String(mainVariantId) &&
        String(
          item.properties?._bound_to_product
        ) === String(mainProductId) &&
        item.properties?._is_hidden_upsell === 'true'
      );
    });

    if (!addon) {
      console.warn(
        'Addon not found:',
        variantId,
        mainVariantId
      );

      return cart;
    }

    const response = await fetch(
      '/cart/change.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: addon.key,
          quantity: 0
        })
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        errorData.description ||
        `Failed to remove addon: ${response.status}`
      );
    }

    return response.json();
  }

  async getCart() {
    const response = await fetch('/cart.js', {
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cart: ${response.status}`
      );
    }

    return response.json();
  }

  handleCartUpdated() {
    document
      .querySelectorAll(
        '.cart-addon-toggle.is-loading'
      )
      .forEach((toggle) => {
        toggle.classList.remove('is-loading');
      });

    this.activeRequests.clear();

    document
      .querySelectorAll(
        '.cart-addon-toggle__error'
      )
      .forEach((error) => {
        error.remove();
      });

    window.helmUpdateCartCount?.();
  }

  showError(toggle, message) {
    if (!toggle) return;

    toggle
      .querySelector('.cart-addon-toggle__error')
      ?.remove();

    const errorDiv =
      document.createElement('div');

    errorDiv.className =
      'cart-addon-toggle__error';

    errorDiv.textContent = message;

    toggle.appendChild(errorDiv);

    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  toggleDetails(header) {
    const toggle = header.closest(
      '.cart-addon-toggle'
    );

    const details = toggle?.querySelector(
      '[data-toggle-details]'
    );

    const arrow = toggle?.querySelector(
      '.cart-addon-toggle__arrow'
    );

    if (!details || !arrow) return;

    const isHidden =
      details.style.display === 'none' ||
      details.style.display === '';

    details.style.display = isHidden
      ? 'block'
      : 'none';

    arrow.classList.toggle(
      'rotated',
      isHidden
    );

    toggle.classList.toggle(
      'expanded',
      isHidden
    );
  }
}

function initCartAddonToggle() {
  if (window.cartAddonToggle) return;

  window.cartAddonToggle =
    new CartAddonToggle();
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initCartAddonToggle,
    { once: true }
  );
} else {
  initCartAddonToggle();
}

document.addEventListener(
  'theme:loaded',
  initCartAddonToggle
);